// src/components/UserMessage.js
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';

import Paper from '@mui/material/Paper';
import {
  Button,
  TextField,
} from '@mui/material';

import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

import { useMessageHandler } from '../contexts/MessageContext.js';
import { transliterateWithMapping } from '../utils/transliteration.js';
import AttachmentEditor from './AttachmentEditor';
import ConfirmationDialog from './ConfirmationDialog';
import PreviewModal from './PreviewModal';
import { FONT_FAMILIES, TEXT_LENGTH_THRESHOLD, MAX_ATTACHMENT_SIZE } from '../constants/appConstants';
import { getBackgroundColor, getMessageFontColors } from '../themes';

const MessageContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  marginBottom: theme.spacing(2),
  width: '100%',
}));

const MessageBubble = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isEditing'
})(({ theme, isEditing }) => ({
  maxWidth: isEditing ? '100%' : '80%',
  minWidth: '20%',
  width: isEditing ? '100%' : 'auto',
  backgroundColor: getBackgroundColor(theme, 'message').user,
  borderRadius: '18px 18px 4px 18px',
  padding: theme.spacing(1.5),
  position: 'relative',
  wordBreak: 'break-word',
  border: `1px solid ${theme.palette.divider}`,
}));

const MessageContent = styled(Typography)(({ theme, fontSize, fontFamily, fontColor }) => {
  const messageFontColors = getMessageFontColors(theme);

  return {
    flexGrow: 1,
    padding: '5px',
    margin: 0,
    overflow: 'auto',
    textAlign: 'left',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    fontFamily: FONT_FAMILIES.find(f => f.id === fontFamily)?.value || FONT_FAMILIES[0].value,
    fontSize: `${fontSize}px`,
    color: fontColor === 'default'
      ? 'inherit'
      : messageFontColors[fontColor] || 'inherit',
    marginBottom: theme.spacing(1),
  };
});

const AttachmentGrid = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  width: '100%',
  marginTop: theme.spacing(1),
}));

const AttachmentPreview = styled(Box, {
  shouldForwardProp: (prop) => !['isImage'].includes(prop)
})(({ theme, isImage }) => ({
  width: isImage ? '64px' : '120px',
  height: isImage ? '64px' : '64px',
  borderRadius: theme.shape.borderRadius,
  cursor: 'pointer',
  border: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'left',
  padding: theme.spacing(1),
  '&:hover': {
    opacity: 0.8,
    transform: 'scale(1.05)',
    transition: 'transform 0.2s ease-in-out',
  },
}));

const DocumentPreview = styled(Box)(({ theme }) => ({
  width: '120px',
  height: '64px',
  borderRadius: theme.shape.borderRadius,
  cursor: 'pointer',
  border: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(1),
  '&:hover': {
    opacity: 0.8,
    transform: 'scale(1.05)',
    transition: 'transform 0.2s ease-in-out',
  },
}));

const UserMessage = ({
  content,
  index,
  fontSize,
  fontFamily,
  fontColor = 'default',
  images,
  textAttachments,
  pdfAttachments,
  selectedEngine,
  selectedService,
  translit,
  currentSession,
  autoOptimizePrompt,
  enableThinking,
}) => {

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [selectedAttachment, setSelectedAttachment] = React.useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState(content);
  const [editedImages, setEditedImages] = useState(images || []);
  const [editedTextAttachments, setEditedTextAttachments] = useState(textAttachments || []);
  const [editedPdfAttachments, setEditedPdfAttachments] = useState(pdfAttachments || []);
  const [isUpdating, setIsUpdating] = useState(false);
  const {
    isProcessing,
    isTranscribing,
    isPlaybackActive,
    handleEditMessageSave,
    showDialog,
    activeEditIndex,
    setActiveEditIndex,
  } = useMessageHandler();

  const { t } = useTranslation();

  // Update when activeEditIndex changes
  useEffect(() => {
    if (activeEditIndex !== index && isEditing) {
      setIsEditing(false);
    }
  }, [activeEditIndex, index]);

  // Also reset on session change
  useEffect(() => {
    if (isEditing) {
      setIsEditing(false);
      setActiveEditIndex(null);
    }
  }, [currentSession]);

  const handleInputChange = (event) => {
    const inputElement = event.target;
    const caretPosition = inputElement.selectionStart;

    if (!translit) {
      setEditedContent(inputElement.value);
      return;
    }

    const { transliteratedText, newCaretPosition } = transliterateWithMapping(inputElement.value, caretPosition);
    setEditedContent(transliteratedText);

    // Set caret position after state update
    setTimeout(() => {
      if (inputElement) {
        inputElement.setSelectionRange(newCaretPosition, newCaretPosition);
      }
    }, 0);
  };

  const handlePaste = async (event) => {
    const items = event.clipboardData.items;
    const imageFiles = [];
    const pdfFiles = [];
    const textItems = [];

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type.indexOf('image') !== -1) {
        const file = item.getAsFile();
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
        const newFile = new File([file], `pasted-image-${timestamp}.png`, {
          type: file.type,
        });
        imageFiles.push(newFile);
      } else if (item.type === 'application/pdf') {
        const file = item.getAsFile();
        pdfFiles.push(file);
      } else if (item.type === 'text/plain') {
        textItems.push(item);
      }
    }

    if (imageFiles.length > 0 || pdfFiles.length > 0) {
      handleAddAttachment([...imageFiles, ...pdfFiles]);
      event.preventDefault();
      return;
    }

    // Handle text paste
    if (textItems.length > 0) {
      event.preventDefault(); // Prevent default paste

      // Get the text content
      const text = event.clipboardData.getData('text/plain');

      // Check text length
      if (text.length > TEXT_LENGTH_THRESHOLD) {
        // Create timestamp for unique filename
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');

        // Check size
        const textSize = new Blob([text]).size;
        if (textSize > MAX_ATTACHMENT_SIZE) {
          alert(`Text is too large (${(textSize / 1024 / 1024).toFixed(2)}MB). Maximum size is ${MAX_ATTACHMENT_SIZE / 1024 / 1024}MB.`);
          return;
        }

        // Create text attachment
        const textAttachment = {
          originalName: `pasted-text-${timestamp}.txt`,
          content: text,
          type: 'text/plain',
          size: textSize
        };

        // Add to text attachments
        setEditedTextAttachments(prev => [...prev, textAttachment]);
      } else {
        // For shorter text, insert at cursor position
        const inputElement = event.target;
        const start = inputElement.selectionStart;
        const end = inputElement.selectionEnd;
        const currentValue = editedContent;
        let newValue = currentValue.substring(0, start) + text + currentValue.substring(end);
        
        // Apply transliteration if enabled
        if (translit) {
          const caretPosition = start + text.length;
          const { transliteratedText, newCaretPosition } = transliterateWithMapping(newValue, caretPosition);
          newValue = transliteratedText;
          setEditedContent(newValue);
          
          // Set cursor position after pasted text
          setTimeout(() => {
            if (inputElement) {
              inputElement.setSelectionRange(newCaretPosition, newCaretPosition);
            }
          }, 0);
        } else {
          setEditedContent(newValue);
          
          // Set cursor position after pasted text
          setTimeout(() => {
            if (inputElement) {
              inputElement.setSelectionRange(start + text.length, start + text.length);
            }
          }, 0);
        }
      }
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    setActiveEditIndex(index);
    setEditedContent(content);

    // Preserve attachment IDs for existing attachments
    setEditedImages((images || []).map(img => ({
      ...img,
      attachmentId: img.attachmentId || img.id
    })));

    setEditedTextAttachments((textAttachments || []).map(att => ({
      ...att,
      attachmentId: att.attachmentId || att.id
    })));

    setEditedPdfAttachments((pdfAttachments || []).map(pdf => ({
      ...pdf,
      attachmentId: pdf.attachmentId || pdf.id
    })));
  };

  const handleSave = async () => {
    if (editedContent.trim() === '') return;

    const restoreState = () => {
      setIsEditing(false);
      setEditedContent(content);
      setEditedImages(images || []);
      setEditedTextAttachments(textAttachments || []);
      setEditedPdfAttachments(pdfAttachments || []);
    };

    showDialog(
      t('editMessage.title'),
      t('editMessage.content'),
      () => {
        handleEditMessageSave(
          editedContent,
          editedImages,
          editedTextAttachments,
          editedPdfAttachments,
          index,
          setIsEditing,
          restoreState,
          selectedEngine,
          selectedService,
          autoOptimizePrompt,
          enableThinking
        );
      },
      restoreState // This will be called if user cancels
    );
  };

  const handleCancel = () => {
    setIsEditing(false);
    setActiveEditIndex(null);
    setEditedContent(content);
    setEditedImages(images || []);
    setEditedTextAttachments(textAttachments || []);
    setEditedPdfAttachments(pdfAttachments || []);
  };

  const handleAddAttachment = (files) => {
    const newFiles = Array.from(files);
    newFiles.forEach(file => {
      const fileType = file.type;
      if (fileType.startsWith('image/')) {
        setEditedImages(prev => [...prev, {
          file,
          originalName: file.name,
          mimetype: file.type,
          preview: URL.createObjectURL(file),
          attachmentId: '' // Empty attachmentId for new attachments
        }]);
      } else if (fileType === 'application/pdf') {
        setEditedPdfAttachments(prev => [...prev, {
          file,
          originalName: file.name,
          mimetype: file.type,
          preview: URL.createObjectURL(file),
          attachmentId: '' // Empty attachmentId for new attachments
        }]);
      } else if (fileType === 'text/plain' || fileType === 'text/markdown') {
        const reader = new FileReader();
        reader.onload = (e) => {
          setEditedTextAttachments(prev => [...prev, {
            content: e.target.result,
            originalName: file.name,
            type: file.type,
            attachmentId: '' // Empty attachmentId for new attachments
          }]);
        };
        reader.readAsText(file);
      }
    });
  };

  const handleRemoveAttachment = (type, index) => {
    switch (type) {
      case 'image':
        setEditedImages(prev => {
          const newImages = [...prev];
          if (newImages[index].preview && newImages[index].isNew) {
            URL.revokeObjectURL(newImages[index].preview);
          }
          newImages.splice(index, 1);
          return newImages;
        });
        break;
      case 'text':
        setEditedTextAttachments(prev => {
          const newAttachments = [...prev];
          newAttachments.splice(index, 1);
          return newAttachments;
        });
        break;
      case 'pdf':
        setEditedPdfAttachments(prev => {
          const newAttachments = [...prev];
          if (newAttachments[index].preview && newAttachments[index].isNew) {
            URL.revokeObjectURL(newAttachments[index].preview);
          }
          newAttachments.splice(index, 1);
          return newAttachments;
        });
        break;
      default:
      // Handle any other types if needed
    }
  };

  const renderImagePreview = (image) => (
    <img
      src={image.preview || image.base64}
      alt={image.originalName || "Preview"}
      style={{
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        borderRadius: '4px',
      }}
    />
  );

  const renderDocumentPreview = (doc, idx) => (
    <DocumentPreview
      key={`${doc.messageId}-${doc.attachmentId || idx}`}
      onClick={() => setSelectedAttachment(doc)}
    >
      {doc.type === 'application/pdf' ? (
        <>
          <PictureAsPdfIcon sx={{ fontSize: 24, mb: 0.5 }} />
          <Typography
            variant="caption"
            align="center"
            sx={{
              width: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {doc.originalName || 'Document'}
          </Typography>
        </>
      ) : (
        <>
          <InsertDriveFileIcon sx={{ fontSize: 24, mb: 0.5 }} />
          <Typography
            variant="caption"
            align="center"
            sx={{
              width: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {doc.originalName || 'Document'}
          </Typography>
        </>
      )}
    </DocumentPreview>
  );

  return (
    <MessageContainer>
      <MessageBubble isEditing={isEditing}>
        {isEditing ? (
          <>
            <TextField
              fullWidth
              multiline
              value={editedContent}
              onChange={handleInputChange}
              onPaste={handlePaste}
              sx={{
                fontSize: `${fontSize}px`,
                fontFamily: FONT_FAMILIES.find(f => f.id === fontFamily)?.value || FONT_FAMILIES[0].value,
                mb: 1
              }}
              autoFocus
              disabled={isUpdating}
            />

            {/* Use the AttachmentEditor component */}
            <AttachmentEditor
              images={editedImages}
              textAttachments={editedTextAttachments}
              pdfAttachments={editedPdfAttachments}
              onAddAttachment={handleAddAttachment}
              onRemoveAttachment={handleRemoveAttachment}
              disabled={isUpdating}
            />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={handleCancel}
                disabled={isUpdating || isProcessing || isTranscribing || isPlaybackActive}
                startIcon={<CancelIcon />}
              >
                {t('cancel')}
              </Button>
              <Button
                size="small"
                variant="contained"
                color="primary"
                onClick={handleSave}
                disabled={isUpdating || isProcessing || isTranscribing || isPlaybackActive}
                startIcon={<SaveIcon />}
              >
                {isUpdating ? t('message.saving') : t('message.save')}
              </Button>
            </Box>
          </>
        ) : (
          <>
            <MessageContent fontSize={fontSize} fontFamily={fontFamily} fontColor={fontColor}>{content}</MessageContent>

            {/* Display image attachments */}
            {images && images.length > 0 && (
              <AttachmentGrid>
                {images.map((image, idx) => (
                  <AttachmentPreview
                    key={`img-${idx}`}
                    isImage={true}
                    onClick={() => setSelectedAttachment(image)}
                  >
                    {renderImagePreview(image)}
                  </AttachmentPreview>
                ))}
              </AttachmentGrid>
            )}

            {/* PDF attachments */}
            {pdfAttachments && pdfAttachments.length > 0 && (
              <AttachmentGrid>
                {pdfAttachments.map((pdf, idx) => (
                  renderDocumentPreview({
                    ...pdf,
                    type: 'application/pdf'
                  }, idx)
                ))}
              </AttachmentGrid>
            )}

            {/* Display text attachments */}
            {textAttachments && textAttachments.length > 0 && (
              <AttachmentGrid>
                {textAttachments.map((doc, idx) => (
                  <AttachmentPreview
                    key={`doc-${idx}`}
                    isImage={false}
                    onClick={() => setSelectedAttachment(doc)}
                  >
                    {renderDocumentPreview(doc, idx)}
                  </AttachmentPreview>
                ))}
              </AttachmentGrid>
            )}

            {selectedAttachment && (
              <PreviewModal
                attachment={selectedAttachment}
                onClose={() => setSelectedAttachment(null)}
              />
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
              <IconButton
                size="small"
                onClick={handleEdit}
                disabled={isProcessing || isTranscribing || isPlaybackActive}
                sx={{ opacity: 0.6, '&:hover': { opacity: 1 } }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Box>
          </>
        )}
      </MessageBubble>

      <ConfirmationDialog
        open={dialogOpen}
        titleKey="confirmationDialog.deleteMessage.title"
        contentKey="confirmationDialog.deleteMessage.content"
        onClose={() => setDialogOpen(false)}
        onConfirm={() => {/* ... */ }}
      />
    </MessageContainer>
  );
};

export default UserMessage;